class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.initializeCartDrawer();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('showCart') === 'true') {
      this.open(document.querySelector('#cart-icon-bubble'));
    }

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay .overlay-bg');
    if (overlay) {
      overlay.addEventListener('click', (evt) => {
        if (evt.target === overlay) {
          this.close();
        }
      });
    }

    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    /** Subscribe to cart updates from anywhere (cart items, product forms, etc.) **/
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
      /** Run the promotion check whenever cart is updated **/

      // TODO: Uncomment this when we are ready to test the promotion (Free Ornament Promotion) - commented due to issues in the holiday period
      // await this.checkCartTotalPromotion();
    });
  }

  disconnectedCallback() {
    /** Clean up subscription when component is removed **/
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getCart() {
    return fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
  }

  getMixpanelDistinctId() {
    try {
      if (window.__mixpanelDistinctId) {
        return window.__mixpanelDistinctId;
      }

      const field = document.getElementById('mixpanel_distinct_id');
      return field && field.value ? field.value : null;
    } catch (error) {
      console.error('[CartDrawer] Error reading Mixpanel distinct id:', error);
      return null;
    }
  }

  initializeCartDrawer() {
    const cartSection = document.querySelector('section[id*="cart_drawer"]');
    if (cartSection?.id) {
      const match = cartSection.id.match(/sections--(.*)/);
      this.cartDrawerId = match ? match[1] : cartSection.id;
    } else {
      const cartDrawerParent = document.querySelector('[id^="CartDrawerParent"]');
      if (cartDrawerParent?.id) {
        this.cartDrawerId = cartDrawerParent.id.split('CartDrawerParent--')[1];
      }
    }

    if (!this.cartDrawerId && this.id) {
      this.cartDrawerId = this.id.split('CartDrawerParent--')[1];
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    
    // TODO: Uncomment this when we are ready to test the promotion (Free Ornament Promotion) - commented due to issues in the holiday period
    // this.checkCartTotalPromotion();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState, shouldOpen = true) {
    if (!parsedState?.sections) {
      try {
        const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
        if (cart?.sections) {
          return this.renderContents(cart);
        }
      } catch (error) {
        console.error('Error fetching cart data:', error);
      }
      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    const sectionsToRender = this.getSectionsToRender();

    try {
      const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const cartCount = cartIconBubble.querySelector('.cart-count-bubble');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
          cartCount.classList.toggle('hidden', cart.item_count === 0);
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }

    sectionsToRender.forEach((section) => {
      if (!section.id || !parsedState.sections[section.id]) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      try {
        const sectionHtml = parsedState.sections[section.id];
        if (!sectionHtml) return;

        /** Parse the HTML to find the specific element we want to update **/
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');

        if (section.selector) {
          /** Find the specific element in the new HTML **/
          const newContent = doc.querySelector(section.selector);
          if (newContent) {
            /** Find the corresponding element in the current DOM **/
            const targetElement = document.querySelector(section.selector);
            if (targetElement) {
              targetElement.innerHTML = newContent.innerHTML;
            }
          }
        } else {
          /** If no specific selector is provided, update the entire section **/
          sectionElement.innerHTML = sectionHtml;
        }
      } catch (error) {
        console.error(`Error updating section ${section.id}:`, error);
      }
    });

    requestAnimationFrame(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay .overlay-bg');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }

      if (shouldOpen) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    try {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const sectionContent = parsedHtml.querySelector(selector);
      return sectionContent?.innerHTML || '';
    } catch (error) {
      console.error('Error parsing HTML:', error);
      return '';
    }
  }

  getSectionsToRender() {
    if (!this.cartDrawerId) {
      this.initializeCartDrawer();
    }

    if (!this.cartDrawerId) {
      console.error('Could not determine cart drawer ID');
      return [];
    }

    const drawerParentElement = document.querySelector(`#CartDrawerParent--sections--${this.cartDrawerId}`);
    const selectorId = drawerParentElement ? `sections--${this.cartDrawerId}` : this.cartDrawerId;

    return [
      {
        id: selectorId,
        selector: '.cart-drawer__items',
        section: 'cart-drawer-items',
      },
      {
        id: selectorId,
        selector: '.cart-drawer__footer',
        section: 'cart-drawer-items',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  async checkCartTotalPromotion() {

    // TODO: Uncomment this when we are ready to test the promotion (Free Ornament Promotion) - commented due to issues in the holiday period
    return;

    /** Prevent concurrent checks **/
    if (this.isCheckingPromotion) return;

    this.isCheckingPromotion = true;

    try {
      const promoSettings = window.gibsonPromo;

      if (
        !promoSettings ||
        !promoSettings.active ||
        !promoSettings.variantId ||
        !promoSettings.threshold ||
        !promoSettings.marketHandle
      ) {
        return;
      }

      const threshold = parseFloat(promoSettings.threshold) * 100;
      const promoVariantId = promoSettings.variantId;

      const cart = await this.getCart();

      /** Calculate subtotal excluding the free product **/
      let cartSubtotal = 0;
      cart.items.forEach((item) => {
        /** Convert variant_id to string for comparison **/
        if (item.variant_id.toString() !== promoVariantId) {
          cartSubtotal += item.final_line_price;
        }
      });

      const marketHandle = window.Shopify.market_handle;

      const isInPromoMarket = marketHandle === promoSettings.marketHandle;

      /** Check if free product is in cart **/
      const freeProductInCart = cart.items.find((item) => item.variant_id.toString() === promoVariantId);
      let requiresShipping = false;
      cart.items.forEach((item) => {
        if (item.requires_shipping && item.variant_id.toString() !== promoVariantId) {
          requiresShipping = true;
        }
      });
      const shouldAddProduct = cartSubtotal >= threshold;

      if (shouldAddProduct && !freeProductInCart && isInPromoMarket && requiresShipping) {
        await this.addPromoProductToCart(promoVariantId, {
          '_is-free-product': 'true',
        });
      } else if (!shouldAddProduct && freeProductInCart && isInPromoMarket) {
        await this.removePromoProductFromCart(promoVariantId);
      } else if (!isInPromoMarket && freeProductInCart) {
        await this.removePromoProductFromCart(promoVariantId);
      } else if(freeProductInCart && !requiresShipping) {
        await this.removePromoProductFromCart(promoVariantId);
      }
    } catch (error) {
      console.error('[CartDrawer] Error checking cart total promotion:', error);
    } finally {
      /** Always reset the flag **/
      this.isCheckingPromotion = false;
    }
  }

  async addPromoProductToCart(variantId, properties = {}) {

    // TODO: Uncomment this when we are ready to test the promotion (Free Ornament Promotion) - commented due to issues in the holiday period
    return;

    // try {
    //   const mixpanelId = this.getMixpanelDistinctId();
    //   const payload = {
    //     items: [
    //       {
    //         id: variantId,
    //         quantity: 1,
    //         properties: properties,
    //       },
    //     ],
    //   };
    //
    //   if (mixpanelId) {
    //     payload.attributes = {
    //       mixpanel_distinct_id: mixpanelId,
    //     };
    //   }
    //
    //   const response = await fetch('/cart/add.js', {
    //     method: 'POST',
    //     body: JSON.stringify(payload),
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //   });

    //   if (!response.ok) {
    //     const errorData = await response.json();
    //     console.error('[CartDrawer] Failed to add product: ', errorData);
    //     return;
    //   }

    //   /** Fetch updated sections using GET **/
    //   const sections = this.getSectionsToRender()
    //     .map((section) => section.id)
    //     .join(',');
    //   const sectionsResponse = await fetch(`${window.Shopify.routes.root}?sections=${sections}`, {
    //     method: 'GET',
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //   });

    //   const sectionsData = await sectionsResponse.json();

    //   /** Update DOM directly without calling renderContents (which would trigger another check) **/
    //   this.getSectionsToRender().forEach((section) => {
    //     if (!section.id || !sectionsData[section.id]) return;

    //     const targetElement = section.selector
    //       ? document.querySelector(section.selector)
    //       : document.getElementById(section.id);

    //     if (!targetElement) return;

    //     const parser = new DOMParser();
    //     const doc = parser.parseFromString(sectionsData[section.id], 'text/html');

    //     if (section.selector) {
    //       const newContent = doc.querySelector(section.selector);
    //       if (newContent) {
    //         targetElement.innerHTML = newContent.innerHTML;
    //       }
    //     } else {
    //       targetElement.innerHTML = sectionsData[section.id];
    //     }
    //   });
    // } catch (error) {
    //   console.error('[CartDrawer] Error adding free product:', error);
    // }
  }

  async removePromoProductFromCart(variantId) {
    try {
      /** Use cart/update.js to remove by variant ID directly **/
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            [variantId]: 0,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[CartDrawer] Failed to remove product:', errorData);
        return;
      }

      /** Fetch updated sections using GET **/
      const sections = this.getSectionsToRender()
        .map((section) => section.id)
        .join(',');
      const sectionsResponse = await fetch(`${window.Shopify.routes.root}?sections=${sections}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const sectionsData = await sectionsResponse.json();

      /** Update DOM directly without calling renderContents (which would trigger another check) **/
      this.getSectionsToRender().forEach((section) => {
        if (!section.id || !sectionsData[section.id]) return;

        const targetElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!targetElement) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionsData[section.id], 'text/html');

        if (section.selector) {
          const newContent = doc.querySelector(section.selector);
          if (newContent) {
            targetElement.innerHTML = newContent.innerHTML;
          }
        } else {
          targetElement.innerHTML = sectionsData[section.id];
        }
      });
    } catch (error) {
      console.error('[CartDrawer] Error removing free product:', error);
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Cart Drawer Items is in cart.js
